import { Close as CloseIcon } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, TextField } from "@mui/material";
import { type ChangeEvent, type FC, type KeyboardEvent, useCallback } from "react";
import { CharCounter, maxCharsDefault } from "./CharCounter";

type Props = {
	placeholder?: string;
	onChange: (value: string) => void;
	onEnter: () => void;
	value: string | undefined;
	disabled?: boolean;
	maxLines?: number;
	/** Limit shown by the counter; defaults to the plain 1500. */
	maxChars?: number;
};

/**
 * CommunicationTextField is a reusable text input component
 * @param placeholder - Placeholder text for the input field
 * @param value - Current value of the input field
 * @param onChange - Callback function to handle changes in the input field
 * @param onEnter - Callback function to handle the Enter key press
 * @param disabled
 * @param maxLines - Maximum number of lines for the multiline input
 * @param maxChars - Limit shown by the counter (e.g. the lower SMS limit when the text has special characters)
 */
const CommunicationTextField: FC<Props> = ({ placeholder = "Type free text", value, onChange, onEnter, disabled, maxLines = 4, maxChars }) => {
	const handleKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>) => {
			if (event.key === "Enter" && !event.shiftKey) {
				event.preventDefault();
				onEnter();
			}
		},
		[onEnter],
	);

	const handleChange = useCallback(
		(event: ChangeEvent<HTMLInputElement>) => {
			if (event.target.value.length > maxCharsDefault) {
				return;
			}
			onChange(event.target.value ?? "");
		},
		[onChange],
	);

	const handleClear = useCallback(() => {
		onChange("");
	}, [onChange]);

	return (
		<Box
			sx={{
				position: "relative",
			}}
		>
			<TextField
				disabled={disabled}
				value={value}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				placeholder={placeholder}
				fullWidth
				variant="standard"
				multiline
				maxRows={maxLines}
				sx={{
					"&.MuiInputBase-multiline > .MuiInputBase-input": {
						scrollbarWidth: "thin",
						scrollbarColor: "#888 transparent",
					},
				}}
				slotProps={{
					htmlInput: { maxLength: maxCharsDefault },
					input: {
						endAdornment: value ? (
							<InputAdornment position="end">
								<IconButton onClick={handleClear} size={"small"}>
									<CloseIcon fontSize={"inherit"} />
								</IconButton>
							</InputAdornment>
						) : null,
					},
				}}
			/>
			<CharCounter
				text={value}
				maxChars={maxChars}
				sxProps={{
					position: "absolute",
					top: -6,
					right: -4,
				}}
			/>
		</Box>
	);
};

export default CommunicationTextField;
